import { Component, type ErrorInfo, type ReactNode } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

/**
 * A render crash must never blank the screen.
 *
 * React unmounts the whole tree when a render throws, so one bad field left the
 * user staring at white with nothing to report and no way back. That happened:
 * a contact who had never been enriched carried no `phones` array, and reading
 * its length took the entire page down.
 *
 * The underlying data is normalised now, but the boundary stays: the next
 * unexpected shape should cost the user a message and a button, not the app.
 */

interface Props {
  children: ReactNode
  /** Shown so the user can say which screen failed. */
  where?: string
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Goes to the browser console only. Never ship contact fields to a logger.
    console.error('Render failed', {
      where: this.props.where,
      message: error.message,
      stack: info.componentStack,
    })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Alert severity="error">
          <AlertTitle>This screen could not be displayed</AlertTitle>
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography variant="body2">
              Nothing was lost and nothing was charged. Your data is untouched.
            </Typography>
            <Typography variant="caption" color="text.secondary" className="mono">
              {this.props.where ? `${this.props.where}: ` : ''}
              {error.message}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" onClick={() => this.setState({ error: null })}>
                Try again
              </Button>
              <Button size="small" onClick={() => window.location.assign('/')}>
                Back to the dashboard
              </Button>
            </Stack>
          </Stack>
        </Alert>
      </Box>
    )
  }
}
